import React, { lazy } from 'react';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { useAppSelector } from '../../stores/redux.store';
import { useLoginPageViewed } from './hooks/useLoginPageViewed';
import { LoginTooManyAttemptsRecaptchaContextProvider } from './contexts/LoginTooManyAttemptsRecaptchaContext';
import { LoginRecoveryOptionsContextProvider } from './contexts/LoginRecoveryOptionsContext';
import { LoginGoogleOneTapRenderBoundary } from './components/LoginGoogleOneTap';
import { MarketingConsentContextProvider } from './contexts/LoginMarketingConsentContext';
import { useShouldOrchestrate } from '../orchestrator';

import OrchestratedLoginPage from './OrchestratedPage';
import { useSearchParamCodes } from './hooks/utility';
import { RecaptchaContextProvider } from '../../contexts/recaptcha';
import { loginPageId } from './constants';
import AwsCaptchaFailureLimitErrorPage from './AwsCaptchaFailureLimitErrorPage';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';

const BasePage = lazy(() => import('./BasePage'));
const BasePageV2 = lazy(() => import('./BasePageV2'));

/**
 * Abstraction to encapsulate which type of login experience should be rendered.
 *
 * Intended to help migrate the login page from v1 to v2 (targeting /v2/check-username).
 *
 * Needed because:
 *   - adv. cobranding is out of scope for the migration (i.e. 'orchestrated' always uses v1 for now)
 *   - the v2 base login page is gated behind a feature gate
 *
 * `LoginPageMode` allows us to encapsulate these variables in a single helper and use a consistent
 * predicate downstream for:
 *   - selecting `error` from correct redux state slice (relevant for recatpcha state)
 *   - deciding which page composition to render
 */
type LoginPageMode = 'orchestrated' | 'base-v1' | 'base-v2';

const getLoginPageMode = ({
  shouldOrchestrate,
  isBaseLoginV2Enabled,
}: {
  shouldOrchestrate: boolean;
  isBaseLoginV2Enabled: boolean;
}): LoginPageMode => {
  if (shouldOrchestrate) {
    return 'orchestrated';
  }

  return isBaseLoginV2Enabled ? 'base-v2' : 'base-v1';
};

const LoginPage = () => {
  useLoginPageViewed();
  const shouldOrchestrate = useShouldOrchestrate();

  const isBaseLoginV2Enabled = useFeatureGate(
    AidFrontendFeatureGate.ENABLE_BASE_LOGIN_CHECK_USERNAME_V2
  );

  const loginPageMode = getLoginPageMode({
    shouldOrchestrate,
    isBaseLoginV2Enabled,
  });

  const loginError = useAppSelector(state =>
    loginPageMode === 'base-v2' ? state.loginV2?.error : state.login?.error
  );

  const { errorCode } = useSearchParamCodes();

  const error = loginError || errorCode;
  const recaptchaType = error?.code === 'captchaScoreTooLow' ? 'checkbox' : 'score';

  if (loginError?.code === 'aws_captcha_failure_limit') {
    return <AwsCaptchaFailureLimitErrorPage />;
  }

  return (
    <LoginTooManyAttemptsRecaptchaContextProvider
      loginStateSource={loginPageMode === 'base-v2' ? 'v2' : 'v1'}>
      <LoginRecoveryOptionsContextProvider>
        <LoginGoogleOneTapRenderBoundary>
          <MarketingConsentContextProvider>
            <RecaptchaContextProvider
              pageId={loginPageId}
              action="checkUsername"
              type={recaptchaType}>
              {loginPageMode === 'orchestrated' ? (
                <OrchestratedLoginPage />
              ) : loginPageMode === 'base-v2' ? (
                <BasePageV2 />
              ) : (
                <BasePage />
              )}
            </RecaptchaContextProvider>
          </MarketingConsentContextProvider>
        </LoginGoogleOneTapRenderBoundary>
      </LoginRecoveryOptionsContextProvider>
    </LoginTooManyAttemptsRecaptchaContextProvider>
  );
};

export default LoginPage;
